import React from 'react'
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro'
import { gridFormatters, gridValueGetters } from 'src/utils/grid'
import { Payment } from 'src/interfaces/payment'
import { PaymentStatusChip } from 'src/components/payment/grid/PaymentStatusChip'
import { PaymentsItemActionButton } from 'src/components/payment/grid/action/PaymentsItemActionButton'
import { Typography } from '@mui/material'
import { PaymentFailureReasonTooltip } from 'src/components/payment/PaymentFailureReasonTooltip'
import tw from 'twin.macro'
import { PaymentsTableFields, PaymentStatus } from './enums'
import { TitledTooltip } from 'src/components/shared/TitledTooltip'
import { colors } from 'src/theme'
import { RiskMinor } from '@shopify/polaris-icons'
import { PayoutMethodTitles, paymentTableHeaderTitles } from './constants'
import { LimitedStringChip } from 'src/components/shared/LimitedStringChip'

const paymentStatusesToShowDetails = [PaymentStatus.Failed, PaymentStatus.AutoRetry]

export const paymentColumns: Array<GridColDef> = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    resizable: false,
  },
  {
    field: PaymentsTableFields.partnerDriverExternalId,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.partnerDriverExternalId],
    sortable: true,
    flex: 2,
    minWidth: 120,
  },
  {
    field: PaymentsTableFields.name,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.name],
    flex: 2,
    minWidth: 140,
    sortable: true,
    renderCell: (params: GridRenderCellParams<Payment>) =>
      params.row.paidInGracePeriod ? (
        <TitledTooltip
          title={params.value}
          content={
            <Typography variant="body2" color={colors.SHADES_GREY_300}>
              This contractor is deactivated but settlement was processed because they are still
              within the 14 days grace period. Without reactivation, future settlements to this
              contract may fail. To continue to pay this contractor, they must update their
              documentation to meet compliance requirements.
            </Typography>
          }
        >
          <div css={tw`flex flex-row items-center`}>
            <RiskMinor width={20} height={20} css={tw`mr-1`} fill={colors.COLORS_RED_500} />
            <Typography
              fontWeight={500}
              variant="subtitle1"
              color={colors.COLORS_RED_500}
              css={tw`ml-2 underline`}
            >
              {params.value}
            </Typography>
          </div>
        </TitledTooltip>
      ) : (
        params.value
      ),
  },
  {
    field: PaymentsTableFields.marketName,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.marketName],
    flex: 2,
    minWidth: 120,
    sortable: false,
  },
  {
    field: PaymentsTableFields.onboardingFlow,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.onboardingFlow],
    flex: 2,
    minWidth: 140,
    sortable: false,
    renderCell: (params: GridRenderCellParams<Payment>) =>
      params.value ? <LimitedStringChip text={params.value} /> : null,
  },
  {
    field: PaymentsTableFields.uploadedAt,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.uploadedAt],
    flex: 2,
    minWidth: 140,
    sortable: true,
    type: 'dateTime',
    valueGetter: gridValueGetters.parseDate,
    valueFormatter: gridFormatters.formatShortDate,
  },
  {
    field: PaymentsTableFields.status,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.status],
    flex: 2,
    minWidth: 120,
    sortable: true,
    renderCell: (params: GridRenderCellParams<Payment>) => (
      <div css={tw`flex items-center`}>
        <PaymentStatusChip status={params.value} />
        {paymentStatusesToShowDetails.includes(params.row.status) &&
          !!params.row.failureReasonType && (
            <PaymentFailureReasonTooltip payment={params.row}>
              <Typography
                fontWeight={500}
                variant="subtitle1"
                color="black"
                css={tw`ml-2 underline`}
              >
                Details
              </Typography>
            </PaymentFailureReasonTooltip>
          )}
      </div>
    ),
  },
  {
    field: PaymentsTableFields.paidAt,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.paidAt],
    flex: 2,
    minWidth: 140,
    sortable: true,
    type: 'dateTime',
    valueGetter: gridValueGetters.parseDate,
    valueFormatter: gridFormatters.formatShortDate,
  },
  {
    field: PaymentsTableFields.payoutMethod,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.payoutMethod],
    flex: 2,
    minWidth: 160,
    valueGetter: (params: GridValueGetterParams<Payment>) =>
      PayoutMethodTitles[params.row.payoutMethod] || params.row.payoutMethod || '-',
    sortable: true,
  },
  {
    field: PaymentsTableFields.paid_to,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.paid_to],
    flex: 2,
    minWidth: 160,
    sortable: false,
    valueGetter: (params: GridValueGetterParams<Payment>) =>
      params.row.paidTo?.trim() ||
      `${params.row.destinationType || ''}${
        params.row.destinationLast4 ? ` •• ${params.row.destinationLast4}` : ''
      }` ||
      '-',
  },
  {
    field: PaymentsTableFields.amount,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.amount],
    flex: 1.5,
    minWidth: 100,
    sortable: true,
    valueFormatter: gridFormatters.formatCurrency,
  },
  {
    field: PaymentsTableFields.netAmount,
    headerName: paymentTableHeaderTitles[PaymentsTableFields.netAmount],
    flex: 1.5,
    minWidth: 100,
    sortable: true,
    valueFormatter: gridFormatters.formatCurrency,
  },
  {
    field: 'actions',
    headerName: '',
    width: 68,
    minWidth: 68,
    maxWidth: 68,
    sortable: false,
    disableExport: true,
    resizable: false,
    renderCell: (params: GridRenderCellParams<Payment>) => (
      <PaymentsItemActionButton payment={params.row} />
    ),
  },
]

export const paymentPageSizeOptions = [100, 250, 500, 1000]
